import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import ChartCard from './components/ChartCard'
import ModuleAudienceCard from './components/ModuleAudienceCard'
import Section from './components/Section'
import StatTilesCard from './components/StatTilesCard'
import { AXIS_TICK, CHART_COLORS, TOOLTIP_STYLE } from './constants'
import { OPERATION_PRODUCT_ANALYTICS_STREAM_CAST } from './operations-gql/queries'
import { ProductAnalyticsPeriod, ProductAnalyticsVariables, StreamCastActivity, StreamCastQuery } from './types'

type StreamCastSectionProps = {
  period: ProductAnalyticsPeriod
}

type ActivityTile = {
  key: keyof StreamCastActivity
  labelKey: string
  suffix?: string
}

type OperationAction = keyof Pick<StreamCastActivity, 'rebooted' | 'checkedOut' | 'replaced' | 'messages'>

const TRANSLATION_PREFIX = 'stats.product_analytics.stream_cast'

const OPERATION_ACTIONS: { key: OperationAction, labelKey: string }[] = [
  { key: 'rebooted', labelKey: 'rebooted' },
  { key: 'checkedOut', labelKey: 'checked_out' },
  { key: 'replaced', labelKey: 'replaced' },
  { key: 'messages', labelKey: 'messages' },
]

const CONFIG_TILES: ActivityTile[] = [
  { key: 'configStarted', labelKey: 'config_started' },
  { key: 'configCompleted', labelKey: 'config_completed' },
  { key: 'configCompletionRate', labelKey: 'config_completion_rate', suffix: ' %' },
  { key: 'configVideoOpened', labelKey: 'config_video_opened' },
]

const FAILURE_TILES: ActivityTile[] = [
  { key: 'failed', labelKey: 'failed' },
  { key: 'failureRate', labelKey: 'failure_rate', suffix: ' %' },
]

const StreamCastSection: React.FC<StreamCastSectionProps> = ({ period }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery<StreamCastQuery, ProductAnalyticsVariables>(
    OPERATION_PRODUCT_ANALYTICS_STREAM_CAST,
    { variables: { period }, errorPolicy: 'all' }
  )
  const audience = data?.productAnalytics?.streamCastAudience
  const activity = data?.productAnalytics?.streamCastActivity

  const operations = useMemo(() => (
    OPERATION_ACTIONS.map(({ key, labelKey }) => ({
      label: t(`${TRANSLATION_PREFIX}.operations.${labelKey}`),
      value: activity?.[key] ?? 0,
    }))
  ), [activity, t])

  const hasFieldError = (field: unknown) => Boolean(error) && !field

  const toStatTiles = (tiles: ActivityTile[]) => tiles.map(({ key, labelKey, suffix }) => ({
    key,
    label: t(`${TRANSLATION_PREFIX}.${labelKey}`),
    value: activity?.[key],
    suffix,
  }))

  return (
    <Section id="product-analytics-stream-cast" title={t(`${TRANSLATION_PREFIX}.title`)} columns={{ xs: 1, md: 2, lg: 4 }}>
      <ModuleAudienceCard
        description={t(`${TRANSLATION_PREFIX}.audience_description`)}
        audience={audience}
        loading={loading}
        hasError={hasFieldError(audience)}
      />

      <ChartCard
        title={t(`${TRANSLATION_PREFIX}.operations_title`)}
        description={t(`${TRANSLATION_PREFIX}.operations_description`)}
        loading={loading}
        hasError={hasFieldError(activity)}
        isEmpty={!operations.some(({ value }) => value > 0)}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={operations}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} interval={0} />
            <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Bar
              dataKey="value"
              name={t(`${TRANSLATION_PREFIX}.actions`)}
              fill={CHART_COLORS.octonary}
              radius={[6, 6, 0, 0]}
              barSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <StatTilesCard
        title={t(`${TRANSLATION_PREFIX}.config`)}
        description={t(`${TRANSLATION_PREFIX}.config_description`)}
        tiles={toStatTiles(CONFIG_TILES)}
        loading={loading}
        hasError={hasFieldError(activity)}
      />

      <StatTilesCard
        title={t(`${TRANSLATION_PREFIX}.failures`)}
        description={t(`${TRANSLATION_PREFIX}.failures_description`)}
        tiles={toStatTiles(FAILURE_TILES)}
        loading={loading}
        hasError={hasFieldError(activity)}
      />
    </Section>
  )
}

export default StreamCastSection
